import { useForm, useStore } from '@tanstack/react-form'
import { Link } from '@tanstack/react-router'
import {
  useCreateCallsignBreak,
  useDeleteCallsignBreak,
  useUpdateCallsignBreak,
} from '@transreport/pauk-rrms-api'
import type {
  CreateCallsignBreakRequest,
  UpdateCallsignBreakRequest,
} from '@transreport/pauk-rrms-api'
import { format, isSameDay } from 'date-fns'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppButton, Modal, UnsavedChangesBlocker } from '@/components/framework'
import { getApiErrorMessage } from '@/lib/apiErrors'
import { UNASSIGNED_ROW_ID } from '@/lib/timeline/constants'
import { minutesToHHmm, parseTimeToMinutes } from '@/lib/timeline/time'
import type { CallsignRow } from '@/types/timeline'

interface AddBreakModalProps {
  isOpen: boolean
  onClose: () => void
  callsigns: CallsignRow[]
  date: Date
  initialBreak?: {
    id: string
    callsignId: string
    startTime: string
    endTime: string
    reason?: string | null
  } | null
  onBreakCreated?: () => void
  onBreakUpdated?: () => void
  onBreakDeleted?: () => void
}

const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'))

const DEFAULT_FORM_VALUES = {
  selectedCallsignId: '',
  startHour: '09',
  startMinute: '00',
  endHour: '09',
  endMinute: '30',
  reason: '',
}

const getDefaultFormValues = (date: Date) => {
  const now = new Date()
  const nowMinutes = now.getHours() * 60 + now.getMinutes()
  const startMinutes = isSameDay(date, now)
    ? Math.min(Math.ceil(nowMinutes / 5) * 5, 23 * 60 + 25)
    : 9 * 60
  const [startHour, startMinute] = minutesToHHmm(startMinutes).split(':')
  const [endHour, endMinute] = minutesToHHmm(Math.min(startMinutes + 30, 23 * 60 + 55)).split(':')

  return {
    ...DEFAULT_FORM_VALUES,
    startHour,
    startMinute,
    endHour,
    endMinute,
  }
}

const isMissingScheduleSetupMessage = (message: string): boolean => {
  const normalized = message.toLowerCase()
  const mentionsSetupSubject =
    normalized.includes('schedule') ||
    normalized.includes('callsign') ||
    normalized.includes('call sign')
  const mentionsMissingSetup =
    normalized.includes('default') ||
    normalized.includes('missing') ||
    normalized.includes('not found') ||
    normalized.includes('not configured') ||
    normalized.includes('setup') ||
    normalized.includes('set up')

  return mentionsSetupSubject && mentionsMissingSetup
}

export const AddBreakModal: React.FC<AddBreakModalProps> = ({
  isOpen,
  onClose,
  callsigns,
  date,
  initialBreak,
  onBreakCreated,
  onBreakUpdated,
  onBreakDeleted,
}) => {
  const { t } = useTranslation()
  const form = useForm({ defaultValues: DEFAULT_FORM_VALUES })
  const formValues = useStore(form.store, (state) => state.values)
  const isDirty = useStore(form.store, (state) => state.isDirty)
  const [showScheduleSetupHelp, setShowScheduleSetupHelp] = useState(false)
  const availableCallsigns = useMemo(
    () => callsigns.filter((callsign) => callsign.id !== UNASSIGNED_ROW_ID),
    [callsigns]
  )

  // Reset form when modal is opened
  useEffect(() => {
    if (isOpen) {
      setShowScheduleSetupHelp(false)
      if (initialBreak) {
        const [startHour, startMinute] = initialBreak.startTime.split(':')
        const [endHour, endMinute] = initialBreak.endTime.split(':')
        form.reset(
          {
            selectedCallsignId: initialBreak.callsignId,
            startHour: startHour ?? DEFAULT_FORM_VALUES.startHour,
            startMinute: startMinute ?? DEFAULT_FORM_VALUES.startMinute,
            endHour: endHour ?? DEFAULT_FORM_VALUES.endHour,
            endMinute: endMinute ?? DEFAULT_FORM_VALUES.endMinute,
            reason: initialBreak.reason ?? '',
          },
          { keepDefaultValues: true }
        )
      } else {
        form.reset(getDefaultFormValues(date), { keepDefaultValues: true })
      }
    }
  }, [date, form, initialBreak, isOpen])

  const formattedDate = useMemo(() => format(date, 'yyyy-MM-dd'), [date])
  const startTime = `${formValues.startHour}:${formValues.startMinute}`
  const endTime = `${formValues.endHour}:${formValues.endMinute}`
  const startMinutes = parseTimeToMinutes(startTime)
  const endMinutes = parseTimeToMinutes(endTime)
  const hasCompleteTimeRange = Boolean(
    formValues.startHour && formValues.startMinute && formValues.endHour && formValues.endMinute
  )
  const isTimeRangeInvalid =
    hasCompleteTimeRange && startMinutes != null && endMinutes != null && startMinutes >= endMinutes
  const timeRangeErrorId = 'add-break-time-range-error'
  const hasSelectedAvailableCallsign = availableCallsigns.some(
    (callsign) => callsign.id === formValues.selectedCallsignId
  )

  const createBreakMutation = useCreateCallsignBreak({
    mutation: {
      meta: {
        toast: { success: t('shiftTimeline.addBreakModal.createSuccess') },
        analytics: { event: 'break_created', area: 'timeline' },
      },
      onSuccess: () => {
        if (onBreakCreated) {
          onBreakCreated()
        }
      },
    },
  })

  const updateBreakMutation = useUpdateCallsignBreak({
    mutation: {
      meta: {
        toast: { success: t('shiftTimeline.addBreakModal.updateSuccess') },
        analytics: { event: 'break_updated', area: 'timeline' },
      },
      onSuccess: () => {
        onBreakUpdated?.()
      },
    },
  })

  const deleteBreakMutation = useDeleteCallsignBreak({
    mutation: {
      meta: {
        toast: { success: t('shiftTimeline.addBreakModal.deleteSuccess') },
        analytics: { event: 'break_deleted', area: 'timeline' },
      },
      onSuccess: () => {
        onBreakDeleted?.()
      },
    },
  })

  const isConfirmDisabled =
    !hasSelectedAvailableCallsign ||
    createBreakMutation.isPending ||
    updateBreakMutation.isPending ||
    deleteBreakMutation.isPending ||
    !formValues.startHour ||
    !formValues.startMinute ||
    !formValues.endHour ||
    !formValues.endMinute ||
    isTimeRangeInvalid

  const showMissingSetupMessage = availableCallsigns.length === 0 || showScheduleSetupHelp

  const handleMutationError = async (error: unknown) => {
    const fallback = t('shiftTimeline.addBreakModal.saveError')
    const message = (await getApiErrorMessage(error)) ?? fallback

    if (isMissingScheduleSetupMessage(message)) {
      setShowScheduleSetupHelp(true)
      toast.error(t('shiftTimeline.addBreakModal.missingScheduleSetupToast'))
      return
    }

    toast.error(message)
  }

  const handleConfirm = async () => {
    if (isConfirmDisabled) return

    const payload: CreateCallsignBreakRequest = {
      callsign_id: formValues.selectedCallsignId,
      operating_date: formattedDate,
      start_time: startTime,
      end_time: endTime,
      reason: formValues.reason.trim() || undefined,
    }

    if (initialBreak) {
      const updatePayload: UpdateCallsignBreakRequest = payload
      try {
        await updateBreakMutation.mutateAsync({ id: initialBreak.id, data: updatePayload })
      } catch (error) {
        await handleMutationError(error)
      }
      return
    }

    try {
      await createBreakMutation.mutateAsync({ data: payload })
    } catch (error) {
      await handleMutationError(error)
    }
  }

  const handleClose = () => {
    if (
      createBreakMutation.isPending ||
      updateBreakMutation.isPending ||
      deleteBreakMutation.isPending
    )
      return
    onClose()
  }

  const handleDelete = async () => {
    if (!initialBreak || deleteBreakMutation.isPending) return
    try {
      await deleteBreakMutation.mutateAsync({ id: initialBreak.id })
    } catch (error) {
      const message =
        (await getApiErrorMessage(error)) ?? t('shiftTimeline.addBreakModal.deleteError')
      toast.error(message)
    }
  }

  const handleStartTimeChange = (field: 'startHour' | 'startMinute', value: string) => {
    const nextStartTime = {
      startHour: field === 'startHour' ? value : formValues.startHour,
      startMinute: field === 'startMinute' ? value : formValues.startMinute,
    }
    const nextStartMinutes = parseTimeToMinutes(
      `${nextStartTime.startHour}:${nextStartTime.startMinute}`
    )

    form.setFieldValue(field, value)

    if (nextStartMinutes == null || endMinutes == null || endMinutes > nextStartMinutes) return

    const [endHour, endMinute] = minutesToHHmm(Math.min(nextStartMinutes + 30, 23 * 60 + 55)).split(
      ':'
    )
    form.setFieldValue('endHour', endHour)
    form.setFieldValue('endMinute', endMinute)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        initialBreak
          ? t('shiftTimeline.addBreakModal.editTitle')
          : t('shiftTimeline.addBreakModal.title')
      }
      size="md"
    >
      <UnsavedChangesBlocker isDirty={isDirty} />
      <div className="space-y-6">
        {showMissingSetupMessage && (
          <div
            className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            role="alert"
          >
            <p>{t('shiftTimeline.addBreakModal.missingScheduleSetup')}</p>
            <Link
              to="/shifts/default"
              search={{ on: undefined }}
              className="mt-2 inline-flex font-medium text-amber-950 underline underline-offset-2 hover:text-amber-800 focus:ring-2 focus:ring-amber-700 focus:outline-none"
            >
              {t('shiftTimeline.addBreakModal.openScheduleSetup')}
            </Link>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="mb-1 block text-sm font-medium text-gray-700">
              {t('shiftTimeline.addBreakModal.startTime')}
            </div>
            <div className="flex items-center gap-2">
              <div>
                <label htmlFor="add-break-start-hour" className="mb-1 block text-xs text-gray-600">
                  {t('shiftTimeline.addBreakModal.hours')}
                </label>
                <select
                  id="add-break-start-hour"
                  value={formValues.startHour}
                  onChange={(e) => handleStartTimeChange('startHour', e.target.value)}
                  aria-invalid={isTimeRangeInvalid}
                  aria-describedby={isTimeRangeInvalid ? timeRangeErrorId : undefined}
                  className="w-20 rounded-input border border-border-light px-input-padding-x py-input-padding-y text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                >
                  {HOURS.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="add-break-start-minute"
                  className="mb-1 block text-xs text-gray-600"
                >
                  {t('shiftTimeline.addBreakModal.minutes')}
                </label>
                <select
                  id="add-break-start-minute"
                  value={formValues.startMinute}
                  onChange={(e) => handleStartTimeChange('startMinute', e.target.value)}
                  aria-invalid={isTimeRangeInvalid}
                  aria-describedby={isTimeRangeInvalid ? timeRangeErrorId : undefined}
                  className="w-20 rounded-input border border-border-light px-input-padding-x py-input-padding-y text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                >
                  {MINUTES.map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-1 block text-sm font-medium text-gray-700">
              {t('shiftTimeline.addBreakModal.endTime')}
            </div>
            <div className="flex items-center gap-2">
              <div>
                <label htmlFor="add-break-end-hour" className="mb-1 block text-xs text-gray-600">
                  {t('shiftTimeline.addBreakModal.hours')}
                </label>
                <select
                  id="add-break-end-hour"
                  value={formValues.endHour}
                  onChange={(e) => form.setFieldValue('endHour', e.target.value)}
                  aria-invalid={isTimeRangeInvalid}
                  aria-describedby={isTimeRangeInvalid ? timeRangeErrorId : undefined}
                  className="w-20 rounded-input border border-border-light px-input-padding-x py-input-padding-y text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                >
                  {HOURS.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="add-break-end-minute" className="mb-1 block text-xs text-gray-600">
                  {t('shiftTimeline.addBreakModal.minutes')}
                </label>
                <select
                  id="add-break-end-minute"
                  value={formValues.endMinute}
                  onChange={(e) => form.setFieldValue('endMinute', e.target.value)}
                  aria-invalid={isTimeRangeInvalid}
                  aria-describedby={isTimeRangeInvalid ? timeRangeErrorId : undefined}
                  className="w-20 rounded-input border border-border-light px-input-padding-x py-input-padding-y text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                >
                  {MINUTES.map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {isTimeRangeInvalid && (
            <div id={timeRangeErrorId} className="col-span-2 -mt-2 text-sm text-red-600">
              {t('shiftTimeline.addBreakModal.timeRangeError')}
            </div>
          )}

          <div className="col-span-2">
            <label
              htmlFor="add-break-callsign"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t('shiftTimeline.addBreakModal.selectCallsign')}
            </label>
            <select
              id="add-break-callsign"
              value={formValues.selectedCallsignId}
              onChange={(e) => form.setFieldValue('selectedCallsignId', e.target.value)}
              className="text-select h-button-min-height w-full rounded-select border border-border-light px-input-padding-x focus:border-primary focus:ring-2 focus:ring-primary focus:outline-none"
            >
              <option value="">{t('shiftTimeline.addBreakModal.selectCallsignPlaceholder')}</option>
              {availableCallsigns.map((callsign) => (
                <option key={callsign.id} value={callsign.id}>
                  {callsign.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-2">
            <label
              htmlFor="add-break-reason"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t('shiftTimeline.addBreakModal.reason')}
            </label>
            <textarea
              id="add-break-reason"
              value={formValues.reason}
              onChange={(e) => form.setFieldValue('reason', e.target.value)}
              rows={3}
              className="w-full rounded-input border border-border-light px-input-padding-x py-input-padding-y text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              placeholder={t('shiftTimeline.addBreakModal.reasonPlaceholder')}
            />
          </div>
        </div>

        <div className="flex justify-end gap-gap-sm border-t border-border-lighter bg-white pt-4">
          {initialBreak && (
            <AppButton
              variant="danger"
              size="md"
              onClick={handleDelete}
              disabled={deleteBreakMutation.isPending || updateBreakMutation.isPending}
              loading={deleteBreakMutation.isPending}
            >
              {t('shiftTimeline.addBreakModal.delete')}
            </AppButton>
          )}
          <AppButton
            variant="primary"
            size="md"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            loading={createBreakMutation.isPending || updateBreakMutation.isPending}
          >
            {createBreakMutation.isPending || updateBreakMutation.isPending
              ? t('shiftTimeline.addBreakModal.confirming')
              : initialBreak
                ? t('shiftTimeline.addBreakModal.save')
                : t('shiftTimeline.addBreakModal.confirm')}
          </AppButton>
          <AppButton
            variant="secondary"
            size="md"
            onClick={handleClose}
            disabled={
              createBreakMutation.isPending ||
              updateBreakMutation.isPending ||
              deleteBreakMutation.isPending
            }
          >
            {t('shiftTimeline.addBreakModal.close')}
          </AppButton>
        </div>
      </div>
    </Modal>
  )
}
